import { useQuery } from "@tanstack/react-query";

import {
  ApiError,
  invoicesApi,
  type InvoiceDetailDto,
  type InvoiceListItemDto,
} from "@/lib/api";
import { useTenant } from "@/hooks/useTenant";

const THIRTY_SECONDS = 30 * 1000;

/**
 * SFWL-015: query keys for the billing/invoices surface, per the
 * `useBlockAccounts` pattern (see that file's doc-comment) — `tenantId`
 * partitions both, see the tenant-scoping note on `useInvoices` below.
 */
const invoiceKeys = {
  all: ["invoices"] as const,
  list: (tenantId: string) => ["invoices", "list", tenantId] as const,
  detail: (tenantId: string, id: string) => ["invoices", "detail", tenantId, id] as const,
};

/**
 * SFWL-015: the active tenant's invoices, plus children's (labeled
 * `relationship: 'child'`) when the viewer is a parent (AC9).
 *
 * `v1/billing/invoices` is `WriteAccessGuard`-gated server-side on the GET
 * too — a view-only member always 403s. `error` surfaces as an `ApiError`
 * (carries `.status`) so the page can distinguish that 403 from any other
 * failure; `retry: false` avoids React Query's default 3-retry storm on a
 * session that will 403 on every attempt (see `useBlockAccounts`).
 *
 * Tenant scoping (Codex P2, PR #147 review): `v1/billing` is scoped
 * server-side by the globally-attached `x-tenant-id` header (registered
 * asynchronously by `TenantThemeProvider` on subdomain resolution — same
 * pattern as `useStrategies`/`useAssets`). A tenant-agnostic query key let a
 * cold-load request that fires before the header registers cache its 400
 * forever (`retry: false` never revisits it once the tenant resolves), and a
 * tenant switch could otherwise serve another tenant's cached rows from the
 * same key. `tenantId` (`?? "default"`, matching `useAssets`/
 * `useBrokerCredentials`) in the query key means a resolved tenant always
 * gets its own cache entry; `enabled` additionally skips firing the request
 * at all while resolution is `"pending"`, so there's no wasted no-header
 * request to cache in the first place.
 */
export function useInvoices() {
  const tenant = useTenant();
  const tenantId = tenant.id ?? "default";

  const query = useQuery({
    queryKey: invoiceKeys.list(tenantId),
    queryFn: () => invoicesApi.list(),
    enabled: tenant.resolution !== "pending",
    staleTime: THIRTY_SECONDS,
    retry: false,
  });

  return {
    invoices: query.data ?? [],
    isLoading: query.isPending,
    error: query.error as ApiError | null,
  };
}

/**
 * SFWL-015: one invoice's detail (billTo/issuer/lineItems), fetched only
 * once `id` is truthy — the Billing page passes `undefined` for a
 * collapsed row so expanding it is what triggers the request. Same
 * tenant-scoped key + pending-resolution gate as `useInvoices` above.
 */
export function useInvoice(id: string | undefined) {
  const tenant = useTenant();
  const tenantId = tenant.id ?? "default";

  const query = useQuery({
    queryKey: invoiceKeys.detail(tenantId, id ?? ""),
    queryFn: () => invoicesApi.get(id as string),
    enabled: !!id && tenant.resolution !== "pending",
    staleTime: THIRTY_SECONDS,
    retry: false,
  });

  return {
    invoice: query.data,
    isLoading: query.isPending,
    error: query.error as ApiError | null,
  };
}

/**
 * SFWL-015: blob-fetches an invoice's PDF through `app/lib/api.ts` (so the
 * globally-attached `x-tenant-id` header rides along, unlike a bare
 * `<a href>` navigation) and triggers a browser download via a throwaway
 * object URL. `invoiceNumber` is passed in rather than re-derived from the
 * response — the server already names the attachment `{invoiceNumber}.pdf`
 * and the caller has it on hand from the list/detail DTO.
 *
 * Plain async helper, not a query/mutation hook — callers (Billing.tsx)
 * own their own per-row loading state and error toast.
 */
export async function downloadInvoicePdf(
  id: string,
  invoiceNumber: string
): Promise<void> {
  const blob = await invoicesApi.getPdfBlob(id);
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export type { InvoiceDetailDto, InvoiceListItemDto };
export { ApiError };
